/**
 * A selfie from the phone's own camera, made small before it is sent: cropped to a square from
 * the middle, 256 px, JPEG, turned the right way up. About 15 KB, so 33 of them are nothing on a
 * hall hotspot.
 */

const SIDE = 256;
const QUALITY = 0.82;

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; done(): void }> {
  // createImageBitmap applies the camera's rotation; older Safari needs the <img> route, which
  // also applies it
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, done: () => bitmap.close() };
    } catch {
      /* fall through */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/** The picture as a small square JPEG. Throws when the file is not a picture the browser can read. */
export async function shrinkToJpeg(file: Blob): Promise<Blob> {
  const { source, width, height, done } = await decode(file);
  const canvas = document.createElement("canvas");
  try {
    const crop = Math.min(width, height);
    canvas.width = SIDE;
    canvas.height = SIDE;
    const ctx = canvas.getContext("2d");
    if (!ctx || !crop) throw new Error("no picture");
    ctx.drawImage(source, (width - crop) / 2, (height - crop) / 2, crop, crop, 0, 0, SIDE, SIDE);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
    if (!blob) throw new Error("no picture");
    return blob;
  } finally {
    done();
    // Safari keeps a canvas's memory until it is made tiny
    canvas.width = 1;
    canvas.height = 1;
  }
}

/** A drawn stand-in face for the pretend day, steady for a given player. */
export function standInFace(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const skin = ["#f1c9a5", "#c98b5f", "#8d5a3b", "#e0ac84", "#b9774c"][h % 5];
  const hair = ["#111111", "#3a2616", "#111111", "#5a3a1c"][(h >>> 3) % 4];
  const bg = ["#ffe9f3", "#e6fbff", "#f3ffd6", "#fff1d6"][(h >>> 5) % 4];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="${bg}"/>` +
    `<path d="M12 100c2-24 18-34 38-34s36 10 38 34z" fill="#111"/><circle cx="50" cy="44" r="23" fill="${skin}"/>` +
    `<path d="M26 42c-2-22 12-30 26-30s26 9 22 30c-6-10-14-15-24-15s-18 5-24 15z" fill="${hair}"/>` +
    `<circle cx="42" cy="46" r="3" fill="#111"/><circle cx="59" cy="46" r="3" fill="#111"/>` +
    `<path d="M41 56q9 8 19 0" stroke="#111" stroke-width="3" fill="none" stroke-linecap="round"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
